import { isChunkLoadError } from "./isChunkLoadError";
import { reloadAndHalt } from "./reloadPage";

/**
 * Await a dynamic-import loader and return its `default` export. Reloads the page
 * once if the chunk is stale: either the import rejects, or on iOS WebKit it
 * resolves to `undefined`. Use this wherever we read `.default` off an import.
 *
 * `moduleName` is only passed through to the Sentry diagnostics.
 */
export async function loadDefaultOrReload<T>(
  loader: () => Promise<{ default: T } | undefined>,
  moduleName?: string,
): Promise<T> {
  let module: { default: T } | undefined;
  try {
    module = await loader();
  } catch (error) {
    if (isChunkLoadError(error)) {
      return reloadAndHalt({
        reason: "import-rejected",
        module: moduleName,
        cause: error,
      });
    }
    throw error;
  }

  // Not a truthiness check: a real falsy default (0, false, "") must pass through
  // instead of triggering a reload.
  if (module?.default === undefined) {
    return reloadAndHalt({ reason: "resolved-undefined", module: moduleName });
  }
  return module.default;
}
